import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { conditionLabel, formatPrice, timeAgo } from "@/lib/catalog";
import type { CategoryProduct } from "@/lib/catalog";
import { categories } from "@/mocks/categories";
import NewBadge from "@/components/feature/NewBadge";
import SimilarProducts from "@/components/feature/SimilarProducts";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/feature/Toast";
import ProductGallery from "@/pages/product/components/ProductGallery";
import ProductReviews from "@/pages/product/components/ProductReviews";
import StarRating from "@/components/feature/StarRating";
import { fetchSellerRating, EMPTY_RATING, type RatingSummary } from "@/lib/reviews";
import { buildWhatsAppLink, normalizeMediaList } from "@/lib/media";
import { startConversation } from "@/lib/chat";

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<CategoryProduct | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sellerRating, setSellerRating] = useState<RatingSummary>(EMPTY_RATING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addItem } = useCart();
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contacting, setContacting] = useState(false);

  const fetchProduct = useCallback(async () => {
    const numericId = Number(id);
    if (!id || Number.isNaN(numericId)) {
      setError("Publicación no válida.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("products")
        .select("*, profiles(full_name, username, avatar_url, whatsapp)")
        .eq("id", numericId)
        .maybeSingle();
      if (err) throw err;
      if (!data) {
        setError("Esta publicación no existe o ya no está disponible.");
      } else {
        setProduct(data as unknown as CategoryProduct);
        fetchSellerRating((data as { seller_id: string }).seller_id)
          .then(setSellerRating)
          .catch(() => setSellerRating(EMPTY_RATING));
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "No se pudo cargar la publicación.",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const categoryMeta = product
    ? categories.find((cat) => cat.name === product.category)
    : undefined;
  const images = normalizeMediaList(product?.images);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <div className="mx-auto max-w-7xl animate-pulse px-4 py-8 md:px-6">
          <div className="h-4 w-40 rounded bg-background-200" />
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="aspect-square w-full rounded-lg bg-background-100" />
            <div className="space-y-4">
              <div className="h-6 w-3/4 rounded bg-background-200" />
              <div className="h-9 w-1/3 rounded bg-background-200" />
              <div className="h-24 w-full rounded bg-background-100" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-background-50 px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100">
          <i className="ri-error-warning-line text-3xl text-accent-700"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          Publicación no disponible
        </h1>
        <p className="mt-1 max-w-sm text-sm text-foreground-500">
          {error ?? "No encontramos este anuncio."}
        </p>
        <Link
          to="/"
          className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Volver al inicio
        </Link>
      </div>
    );
  }

  const isNew = (product.condition ?? "").toLowerCase() === "nuevo";
  const seller = product.profiles?.full_name ?? "Vendedor";
  const sellerInitial = seller.charAt(0).toUpperCase();
  const sellerAvatar = product.profiles?.avatar_url ?? "";
  const sellerUsername = product.profiles?.username ?? "";
  const sellerProfileLink = sellerUsername ? `/perfil/${sellerUsername}` : "";
  const whatsappNumber = product.profiles?.whatsapp ?? "";
  const whatsappLink = whatsappNumber
    ? buildWhatsAppLink(
        whatsappNumber,
        `Hola, estoy interesado en «${product.title}» que vi en VendeYa.`
      )
    : "";

  async function handleContact() {
    if (!user) {
      navigate("/login");
      return;
    }
    setContacting(true);
    try {
      const convId = await startConversation(product.id);
      navigate(`/mensajes/${convId}`);
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : "No se pudo iniciar la conversación.",
        "error",
      );
    } finally {
      setContacting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
        {/* Migas de pan */}
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer hover:text-primary-600">
            Inicio
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          {categoryMeta ? (
            <Link
              to={`/categoria/${categoryMeta.id}`}
              className="cursor-pointer hover:text-primary-600"
            >
              {product.category}
            </Link>
          ) : (
            <span>{product.category}</span>
          )}
          <i className="ri-arrow-right-s-line"></i>
          <span className="truncate text-foreground-700">{product.title}</span>
        </nav>

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Galería */}
          <ProductGallery
            images={images}
            title={product.title}
            activeIndex={activeImage}
            onIndexChange={setActiveImage}
            isFullscreen={isFullscreen}
            onFullscreenChange={setIsFullscreen}
            overlay={
              <>
                <span
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                    isNew
                      ? "bg-secondary-500 text-white"
                      : "bg-background-800 text-white dark:bg-background-200 dark:text-foreground-950"
                  }`}
                >
                  {conditionLabel(product.condition)}
                </span>
                <NewBadge createdAt={product.created_at} />
              </>
            }
          />

          {/* Información */}
          <div className="flex flex-col">
            <h1 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {product.title}
            </h1>

            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-heading text-3xl font-bold text-primary-600">
                {formatPrice(product.price, product.currency)}
              </span>
              {product.currency && (
                <span className="text-sm text-foreground-500">
                  {product.currency}
                </span>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {product.views ?? 0} vistas
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {timeAgo(product.created_at)}
              </span>
              {categoryMeta && (
                <span className="flex items-center gap-1.5">
                  <i className={categoryMeta.icon}></i>
                  {product.category}
                </span>
              )}
            </div>

            {/* Vendedor */}
            <div className="mt-6 flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-100 p-4">
              {sellerProfileLink ? (
                <Link
                  to={sellerProfileLink}
                  title={`Ver perfil de ${seller}`}
                  className="group flex min-w-0 cursor-pointer items-center gap-3"
                >
                  {sellerAvatar ? (
                    <img
                      src={sellerAvatar}
                      alt={seller}
                      className="h-11 w-11 shrink-0 rounded-full border border-background-200 object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-500 text-base font-semibold text-white">
                      {sellerInitial}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {seller}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-foreground-500">
                        Vendedor en VendeYa
                      </span>
                      {sellerRating.count > 0 && (
                        <StarRating
                          value={sellerRating.average}
                          count={sellerRating.count}
                          size="sm"
                        />
                      )}
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="flex min-w-0 items-center gap-3">
                  {sellerAvatar ? (
                    <img
                      src={sellerAvatar}
                      alt={seller}
                      className="h-11 w-11 shrink-0 rounded-full border border-background-200 object-cover object-top"
                    />
                  ) : (
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-500 text-base font-semibold text-white">
                      {sellerInitial}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-950">
                      {seller}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-foreground-500">
                        Vendedor en VendeYa
                      </span>
                      {sellerRating.count > 0 && (
                        <StarRating
                          value={sellerRating.average}
                          count={sellerRating.count}
                          size="sm"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
              <span className="ml-auto flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-700">
                <i className="ri-verified-badge-line"></i>
                Verificado
              </span>
            </div>

            {/* Descripción */}
            <div className="mt-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                Descripción
              </h2>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground-600">
                {product.description || "El vendedor no agregó una descripción."}
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    addItem(product);
                    showToast("Agregado al carrito", "success");
                  }}
                  className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  <i className="ri-shopping-cart-2-line text-base"></i>
                  Agregar al carrito
                </button>
                <Link
                  to={categoryMeta ? `/categoria/${categoryMeta.id}` : "/"}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <i className="ri-arrow-left-line"></i>
                  Volver a {product.category}
                </Link>
              </div>

              {user?.id !== product.seller_id && (
                <button
                  type="button"
                  onClick={handleContact}
                  disabled={contacting}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {contacting ? (
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                  ) : (
                    <i className="ri-chat-3-line text-lg"></i>
                  )}
                  Enviar mensaje al vendedor
                </button>
              )}

              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-secondary-600"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Contactar por WhatsApp
                </a>
              )}
            </div>

            <ProductReviews
              productId={product.id}
              sellerId={product.seller_id}
            />
          </div>
        </div>
      </main>

      {/* Recomendaciones: el sistema siempre sugiere artículos similares */}
      <div className="border-t border-background-200/70 bg-background-100/50 pb-4">
        <SimilarProducts currentId={product.id} category={product.category} />
      </div>
    </div>
  );
}